import React, { useState } from 'react'
import { inject, observer } from 'mobx-react'
import { CloseIcon } from './Icons'
import OverviewSection from './OverviewSection'
import TrendsChartSection from './TrendsChartSection'
import MetricsSection from './MetricsSection'
import GenderSection from './GenderSection'
import PopulationPyramid from './PopulationPyramid'
import ShareExportDropdown from '../../common/ShareExportDropdown'
// Inner component that uses hooks - receives observables as props from outer observer
const PeopleDataSidebarInner = ({
  onClose,
  rightSidebarStore,
  loading,
  populationTrendsData,
  polygonTrendsData,
  showPolygonLines,
  polygonData,
  targetGroupId,
  selectedLine,
  genderData,
  polygonGenderData,
  totalPopulation,
  growthRate,
  density,
  size,
  targetGroupName,
  singlePolygonData,
  singlePolygonId,
  aggSelector,
  showIndividualPolygonLines,
  areaUnit,
  populationDataset,
  worldpopAvailable,
  worldpopLoading,
  uiStore,
  selectionStore
}) => {
  // Hooks must be called first, before any conditional returns
  const [chartType, setChartType] = useState('line')
  const [expandedChart, setExpandedChart] = useState(null)
  const [pyramidVisibleSeries, setPyramidVisibleSeries] = useState({
    male: true,
    female: true
  })

  const handleTogglePyramidSeries = (seriesKey) => {
    setPyramidVisibleSeries(prev => ({
      ...prev,
      [seriesKey]: !prev[seriesKey]
    }))
  }

  // Generate colors for polygon lines (static)
  const polygonColors = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
  ]

  // Use single polygon data if available, otherwise use polygon gender data if showing individual polygon lines, otherwise use target group data
  const selectedYearNum = (() => {
    const year = rightSidebarStore && (rightSidebarStore.populationSelectedYear || rightSidebarStore.latestYear)
    if (!year) return null
    return typeof year === 'string' ? parseInt(year, 10) : year
  })()
  const safeGenderData = singlePolygonData && singlePolygonData.stats
    ? [{
        id: singlePolygonId,
        name: targetGroupName || 'Polygon',
        male: singlePolygonData.stats.male_population && singlePolygonData.stats.male_population.length > 0
          ? -Math.abs((singlePolygonData.stats.male_population.find(item => item.year === selectedYearNum) || singlePolygonData.stats.male_population[singlePolygonData.stats.male_population.length - 1]).value)
          : 0,
        female: singlePolygonData.stats.female_population && singlePolygonData.stats.female_population.length > 0
          ? (singlePolygonData.stats.female_population.find(item => item.year === selectedYearNum) || singlePolygonData.stats.female_population[singlePolygonData.stats.female_population.length - 1]).value
          : 0
      }]
    : (showPolygonLines && showIndividualPolygonLines && polygonGenderData && polygonGenderData.length > 0)
        ? polygonGenderData
        : (genderData && genderData.length > 0)
            ? genderData
            : [
                {
                  name: '',
                  male: -250000,
                  female: 280000
                }
              ]

  const safeTotalPopulation = totalPopulation
  const safeGrowthRate = growthRate || 0
  const safeDensity = density
  const safeSize = size
  const safeTargetGroupName = targetGroupName
  const hasPeopleData = Boolean(
    (singlePolygonData && singlePolygonData.stats) ||
    (populationTrendsData && populationTrendsData.length > 0) ||
    (polygonData && polygonData.length > 0) ||
    (rightSidebarStore && rightSidebarStore.populationData && rightSidebarStore.populationData.data && rightSidebarStore.populationData.data.length > 0)
  )
  const isAnyLoading = Boolean(loading || (uiStore && uiStore.processing))
  const isDataLoading = Boolean(
    isAnyLoading &&
    !rightSidebarStore.noData &&
    !hasPeopleData
  )
  const isNoDataError = Boolean(
    rightSidebarStore &&
    rightSidebarStore.error === 'No polygon data available'
  )

  // Get year range for subtitle
  const trends = populationTrendsData || []
  const firstYear = trends.length > 0 && trends[0] ? trends[0].year : 2020
  const lastYear = trends.length > 0 && trends[trends.length - 1] ? trends[trends.length - 1].year : 2025
  const yearRange = trends.length > 0 ? `${firstYear}-${lastYear}` : '2020-2025'

  // Calculate MF chart axis domain based on the selected year's data
  const getStableGenderDomainMax = () => {
    let maxValue = 0
    safeGenderData.forEach(item => {
      maxValue = Math.max(maxValue, Math.abs(item.male || 0), item.female || 0)
    })
    return maxValue > 0 ? Math.ceil(maxValue) : 300000
  }

  const genderDomainMax = getStableGenderDomainMax()
  const maleChartDomain = [-genderDomainMax, 0]
  const femaleChartDomain = [0, genderDomainMax]
  const isExpandedMode = expandedChart !== null

  return (
    <div className={`people-data-sidebar ${isExpandedMode ? 'people-data-sidebar--expanded' : ''}`}>
      <div className='people-data-sidebar__header'>
        <div className='people-data-sidebar__header-content' style={{ flex: 1, minWidth: 0, marginRight: isExpandedMode ? 0 : '12px' }}>
          <h2 className='people-data-sidebar__title'>People</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', marginTop: '4px' }}>
            <span className='people-data-sidebar__folder' style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 'calc(100% - 30px)' }}>
              {/* {(singlePolygonData && (singlePolygonData.name || singlePolygonData.title)) || safeTargetGroupName} */}
              {safeTargetGroupName}
            </span>
            <ShareExportDropdown />
          </div>
        </div>
        {!isExpandedMode && (
          <button
            type='button'
            className='people-data-sidebar__close-btn'
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        )}
      </div>

      <div className={`people-data-sidebar__content ${isExpandedMode ? 'people-data-sidebar__content--expanded' : ''} ${(loading || (uiStore && uiStore.processing)) && hasPeopleData && !singlePolygonData && !rightSidebarStore.noData ? 'loading loading-dark' : ''}`}>
        {(showIndividualPolygonLines && !singlePolygonData) && (
          <button
            type='button'
            className='people-data-sidebar__back-to-summary'
            onClick={() => {
              // if (showIndividualPolygonLines && !singlePolygonData && rightSidebarStore && rightSidebarStore.targetGroupId && rightSidebarStore.targetGroupName) {
              //   rightSidebarStore.fetchPopulationDataForTargetGroup(
              //     rightSidebarStore.targetGroupId,
              //     rightSidebarStore.targetGroupName
              //   )
              //   return
              // }
              // rightSidebarStore.clearSinglePolygon()
              if (rightSidebarStore) {
                rightSidebarStore.selectedLine = null
                rightSidebarStore.selectedLinePopulationDatas = null
                rightSidebarStore.showIndividualPolygonLines = false
                rightSidebarStore.showPolygonLines = false
                rightSidebarStore.showIncomePolygonData = false
                rightSidebarStore.restoreSummaryWorldpopAvailability()
              }
            }}
          >
            <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path d='M3.66602 8.0013H12.666' stroke='#0A0A0A' stroke-linecap='round' stroke-linejoin='round' />
              <path d='M7.33394 12.0013C7.33394 12.0013 3.33399 9.05534 3.33398 8.00127C3.33398 6.9472 7.334 4.0013 7.334 4.0013' stroke='#0A0A0A' stroke-linecap='round' stroke-linejoin='round' />
            </svg>
            Back to Summary
          </button>
        )}
        {!rightSidebarStore && (
          <div className='people-data-sidebar__loading'>
            Loading...
          </div>
        )}
        {isDataLoading && (
          <div className='people-data-sidebar__loading people-data-sidebar__loading--spinner'>
            <div className='spinner' />
          </div>
        )}

        {rightSidebarStore && rightSidebarStore.error && !isNoDataError && (
          <div className='people-data-sidebar__error'>
            Error: {rightSidebarStore.error}
          </div>
        )}
        {!isAnyLoading && rightSidebarStore && (isNoDataError || !rightSidebarStore.error) && (rightSidebarStore.noData || !hasPeopleData) && (
          <div className='people-data-sidebar__loading'>
            {isNoDataError ? rightSidebarStore.error : 'No data available'}
          </div>
        )}
        {!isDataLoading && hasPeopleData && !rightSidebarStore.noData && !isNoDataError && (
          <>
            {!isExpandedMode && (
              <OverviewSection
                safeTotalPopulation={safeTotalPopulation}
                safeGrowthRate={safeGrowthRate}
                singlePolygonData={singlePolygonData}
                polygonData={polygonData}
                rightSidebarStore={rightSidebarStore}
              />
            )}
            {(!isExpandedMode || expandedChart === 'trends') && (
              <TrendsChartSection
                chartType={chartType}
                setChartType={setChartType}
                yearRange={yearRange}
                singlePolygonData={singlePolygonData}
                singlePolygonId={singlePolygonId}
                polygonTrendsData={polygonTrendsData}
                populationTrendsData={populationTrendsData}
                showPolygonLines={showPolygonLines}
                showIndividualPolygonLines={showIndividualPolygonLines}
                polygonData={polygonData}
                selectedLine={selectedLine}
                targetGroupId={targetGroupId}
                targetGroupName={targetGroupName}
                aggSelector={aggSelector}
                polygonColors={polygonColors}
                rightSidebarStore={rightSidebarStore}
                populationDataset={populationDataset}
                worldpopAvailable={worldpopAvailable}
                worldpopLoading={worldpopLoading}
                safeTargetGroupName={safeTargetGroupName}
                uiStore={uiStore}
                isExpanded={expandedChart === 'trends'}
                onToggleExpand={() => setExpandedChart(expandedChart === 'trends' ? null : 'trends')}
              />
            )}
            {!isExpandedMode && (
              <MetricsSection
                safeDensity={safeDensity}
                safeSize={safeSize}
                areaUnit={areaUnit}
                rightSidebarStore={rightSidebarStore}
                selectionStore={selectionStore}
              />
            )}
            {(!isExpandedMode || expandedChart === 'gender') && (
              <GenderSection
                safeGenderData={safeGenderData}
                maleChartDomain={maleChartDomain}
                femaleChartDomain={femaleChartDomain}
                genderDomainMax={genderDomainMax}
                selectedLine={selectedLine}
                showPolygonLines={showPolygonLines}
                showIndividualPolygonLines={showIndividualPolygonLines}
                singlePolygonData={singlePolygonData}
                aggSelector={aggSelector}
                rightSidebarStore={rightSidebarStore}
                isExpanded={expandedChart === 'gender'}
                onToggleExpand={() => setExpandedChart(expandedChart === 'gender' ? null : 'gender')}
              />
            )}
            {(!isExpandedMode || expandedChart === 'pyramid') && (
              <PopulationPyramid
                polygonData={polygonData}
                singlePolygonData={singlePolygonData}
                uiStore={uiStore}
                selectionStore={selectionStore}
                rightSidebarStore={rightSidebarStore}
                visibleSeries={pyramidVisibleSeries}
                onToggleSeries={handleTogglePyramidSeries}
                isExpanded={expandedChart === 'pyramid'}
                onToggleExpand={() => setExpandedChart(expandedChart === 'pyramid' ? null : 'pyramid')}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

// Outer component wrapped with observer - accesses observables and passes as props
// This pattern works with React 16.9.0 and mobx-react 4.2.2
const PeopleDataSidebar = observer(({ onClose, rightSidebarStore, uiStore, selectionStore }) => {
  // Access observables here so observer can track them
  const loading = rightSidebarStore.loading
  const populationTrendsData = rightSidebarStore.populationTrendsForChart
  const polygonTrendsData = rightSidebarStore.polygonTrendsForChart
  const showPolygonLines = rightSidebarStore.showPolygonLines
  const showIndividualPolygonLines = rightSidebarStore.showIndividualPolygonLines
  const polygonData = rightSidebarStore.polygonData || []
  const targetGroupId = rightSidebarStore.targetGroupId
  const selectedLine = rightSidebarStore.selectedLine
  const genderData = rightSidebarStore.genderDataForChart
  const polygonGenderData = rightSidebarStore.polygonGenderDataForChart
  const totalPopulation = rightSidebarStore.totalPopulation
  const growthRate = rightSidebarStore.growthRate
  const density = rightSidebarStore.density
  const size = rightSidebarStore.size
  const targetGroupName = rightSidebarStore.targetGroupName
  const singlePolygonData = rightSidebarStore.singlePolygonData
  const singlePolygonId = rightSidebarStore.singlePolygonId
  const aggSelector = rightSidebarStore.aggSelector
  const areaUnit = rightSidebarStore.areaUnit
  const populationDataset = rightSidebarStore.populationDataset
  const worldpopAvailable = rightSidebarStore.worldpopAvailable
  const worldpopLoading = rightSidebarStore.worldpopLoading

  return (
    <PeopleDataSidebarInner
      onClose={onClose}
      rightSidebarStore={rightSidebarStore}
      loading={loading}
      populationTrendsData={populationTrendsData}
      polygonTrendsData={polygonTrendsData}
      showPolygonLines={showPolygonLines}
      polygonData={polygonData}
      targetGroupId={targetGroupId}
      selectedLine={selectedLine}
      genderData={genderData}
      polygonGenderData={polygonGenderData}
      totalPopulation={totalPopulation}
      growthRate={growthRate}
      density={density}
      size={size}
      targetGroupName={targetGroupName}
      singlePolygonData={singlePolygonData}
      singlePolygonId={singlePolygonId}
      aggSelector={aggSelector}
      showIndividualPolygonLines={showIndividualPolygonLines}
      areaUnit={areaUnit}
      populationDataset={populationDataset}
      worldpopAvailable={worldpopAvailable}
      worldpopLoading={worldpopLoading}
      uiStore={uiStore}
      selectionStore={selectionStore}
    />
  )
})

export default inject('rightSidebarStore', 'uiStore', 'selectionStore')(PeopleDataSidebar)
